import React, { useState, useEffect } from "react";
import { Download, FileUp, ShieldCheck, ExternalLink, FileJson, FileSpreadsheet } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { getAllUsersRequest } from "../../../redux/actions/userActions";
import { payrollFetchResourceRequest } from "../../../redux/actions/payrollActions";
import * as XLSX from "xlsx";
import "./PaieCyclePages.css";

export default function TeledeclarationsSocialesPage() {
    const dispatch = useDispatch();
    const users = useSelector((state) => state.user?.users || []);
    const { historiqueSalaires = [] } = useSelector((state) => state.payroll || {});

    const [fileUploaded, setFileUploaded] = useState(false);
    const [fileName, setFileName] = useState("");

    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(payrollFetchResourceRequest("historiqueSalaires"));
    }, [dispatch]);

    const handleUploadClick = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".xls,.xlsx,.csv";
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                setFileName(file.name);
                setFileUploaded(true);
            }
        };
        input.click();
    };

    const downloadExcel = (populateWithData) => {
        const headers = ["C.N.S.S", "NOM", "PRENOM", "C.I.N", "JOUR", "SALAIRE DH", "SITUATION"];
        let data = [];

        if (populateWithData) {
            data = users.map(user => {
                // Find current salary
                const history = historiqueSalaires
                    .filter(h => h.userId === user.id)
                    .sort((a, b) => new Date(b.dateDebut) - new Date(a.dateDebut));
                const currentSalary = history.length > 0 ? history[0].salaireBase : 0;

                return {
                    "C.N.S.S": user.numCnss || "",
                    "NOM": user.lastName || "",
                    "PRENOM": user.firstName || "",
                    "C.I.N": user.cin || "",
                    "JOUR": 26,
                    "SALAIRE DH": Number(currentSalary).toFixed(2),
                    "SITUATION": "Actif" // Can be mapped to actual user status if available
                };
            });
        }

        const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Salaires");

        const filename = populateWithData ? "pre_etabli_damancom.xlsx" : "template_vide_damancom.xlsx";
        XLSX.writeFile(workbook, filename);
    };

    const handleGenerateXML = () => {
        // Damancom DS XML Structure (Dummy data for demo)
        const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<DeclarationSalaires>
    <Entreprise>
        <RaisonSociale>BlueScratch SARL</RaisonSociale>
        <NumAffiliation>1234567</NumAffiliation>
        <MoisDeclaration>10</MoisDeclaration>
        <AnneeDeclaration>2026</AnneeDeclaration>
    </Entreprise>
    <Salaries>
        <Salarie>
            <MatriculeCNSS>987654321</MatriculeCNSS>
            <Nom>Doe</Nom>
            <Prenom>John</Prenom>
            <CIN>AB123456</CIN>
            <JoursTravailles>26</JoursTravailles>
            <SalaireBrut>15000.00</SalaireBrut>
        </Salarie>
    </Salaries>
</DeclarationSalaires>`;

        const blob = new Blob([xmlContent], { type: "application/xml" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `DS_Damancom_${new Date().getTime()}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleGoToDamancom = () => {
        window.open("https://www.damancom.ma/", "_blank", "noopener,noreferrer");
    };

    const btnPrimary = { background: "#204385", color: "#fff", border: "none" };
    const btnSecondary = { background: "#334785", color: "#fff", border: "none" };
    const btnOutline = { background: "transparent", color: "#204385", border: "1px solid #204385" };

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><ShieldCheck size={18} /> Télédéclarations Sociales (Damancom)</h2>
                    <p>Génération du fichier de déclaration des salaires (DS) pour la CNSS.</p>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                    <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => downloadExcel(false)}>
                        <FileSpreadsheet size={15} /> Télécharger la template
                    </button>
                    <button className="pcyc-btn pcyc-btn-primary" type="button" onClick={() => downloadExcel(true)}>
                        <Download size={15} /> Générer le pré-établi
                    </button>
                </div>
            </div>

            <div className="pcyc-grid">
                <section className="pcyc-card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px", gap: "20px" }}>
                    <div style={{ background: "#e8edf7", color: "#204385", borderRadius: "50%", padding: "20px" }}>
                        <ShieldCheck size={48} />
                    </div>
                    <div style={{ textAlign: "center", maxWidth: "600px" }}>
                        <h3>Déclaration Mensuelle des Salaires (DS)</h3>
                        <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "8px", lineHeight: "1.6" }}>
                            Étape 1 : Téléchargez le fichier pré-établi et complétez-le si nécessaire.<br />
                            Étape 2 : Importez le fichier validé ci-dessous.<br />
                            Étape 3 : Générez le fichier XML et déposez-le sur le portail Damancom.
                        </p>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", alignItems: "center", marginTop: "10px", width: "100%", maxWidth: "400px" }}>
                        
                        {!fileUploaded ? (
                            <button className="pcyc-btn" style={{ ...btnOutline, padding: "12px 24px", fontSize: "14px", width: "100%", justifyContent: "center" }} type="button" onClick={handleUploadClick}>
                                <FileUp size={18} /> Importer le fichier source
                            </button>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
                                <div style={{ background: "#eef2ff", padding: "12px", borderRadius: "8px", textAlign: "center", color: "#4f46e5", fontSize: "13px", fontWeight: "500" }}>
                                    Fichier chargé : {fileName}
                                </div>
                                <button className="pcyc-btn" style={{ ...btnPrimary, padding: "12px 24px", fontSize: "14px", width: "100%", justifyContent: "center" }} type="button" onClick={handleGenerateXML}>
                                    <FileJson size={18} /> Générer le fichier XML Damancom
                                </button>
                            </div>
                        )}

                        <div style={{ height: "1px", background: "#e5e7eb", width: "100%", margin: "8px 0" }}></div>

                        <button className="pcyc-btn" style={{ ...btnSecondary, padding: "12px 24px", fontSize: "14px", width: "100%", justifyContent: "center" }} type="button" onClick={handleGoToDamancom}>
                            <ExternalLink size={18} /> Accéder au portail Damancom
                        </button>

                    </div>
                </section>
            </div>
        </div>
    );
}
